import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { 
  AlertTriangle, 
  ArrowLeft,
  RefreshCw,
  Mail,
  Phone
} from 'lucide-react';

const PaymentCancelled = () => {
  const handleRetryPayment = () => {
    // Go back to the application or try payment again
    window.location.href = '/';
  };

  const handleContactSupport = () => {
    window.location.href = 'mailto:support@legacypropertycouncil.com?subject=Payment Issue - Need Assistance';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white py-12">
      <div className="container mx-auto px-4 max-w-2xl">
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-12 h-12 text-amber-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-800 mb-4">
            Payment Cancelled
          </h1>
          <p className="text-lg text-slate-600 max-w-xl mx-auto">
            Your payment process was cancelled and your membership is not yet activated. 
            Don't worry - your application is still saved and you can complete payment anytime.
          </p>
        </div>

        {/* Status Card */}
        <Card className="shadow-lg mb-8">
          <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50">
            <CardTitle className="flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <span>Current Status</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Application Status:</span>
                <span className="font-medium text-blue-600">Approved & Contract Signed</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Payment Status:</span>
                <span className="font-medium text-amber-600">Pending</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Membership Status:</span>
                <span className="font-medium text-slate-600">Awaiting Payment</span>
              </div>
            </div>
            
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <p className="text-blue-800 text-sm">
                <strong>Good News:</strong> Your PMA contract is already signed and a copy has been sent to your email. 
                You just need to complete the membership payment to activate your account.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Next Steps */}
        <Card className="shadow-lg mb-8">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-green-50">
            <CardTitle>What Would You Like to Do?</CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            
            <Button 
              onClick={handleRetryPayment}
              className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white py-3"
            >
              <RefreshCw className="w-5 h-5 mr-2" />
              Try Payment Again
            </Button>
            
            <div className="flex space-x-4">
              <Button 
                onClick={() => window.location.href = '/'}
                variant="outline"
                className="flex-1"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Return to Home
              </Button>
              
              <Button 
                onClick={handleContactSupport}
                variant="outline"
                className="flex-1"
              >
                <Mail className="w-4 h-4 mr-2" />
                Contact Support
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Help Information */}
        <Card className="shadow-lg bg-slate-50">
          <CardHeader>
            <CardTitle className="text-slate-800">Need Help?</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-slate-800">Email Support</h4>
                  <p className="text-sm text-slate-600">
                    Send us an email at{' '}
                    <a href="mailto:support@legacypropertycouncil.com" className="text-blue-600 hover:underline">
                      support@legacypropertycouncil.com
                    </a>
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-slate-800">Phone Support</h4>
                  <p className="text-sm text-slate-600">
                    Call us at{' '}
                    <a href="tel:+13125555532" className="text-green-600 hover:underline">
                      (312) 555-LEGACY
                    </a>
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-amber-50 border-l-4 border-amber-500">
              <p className="text-sm text-amber-800">
                <strong>Payment Issues?</strong> If you're experiencing technical difficulties with payment, 
                please contact our support team. We can help process your membership manually or assist 
                with alternative payment methods.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Additional Information */}
        <div className="text-center mt-8">
          <p className="text-xs text-slate-500">
            Your application data is securely saved. You can complete payment at any time to activate your membership.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentCancelled;